import {Component, EventEmitter, Input, Output} from '@angular/core';
import {faCheck, faInfoCircle, faSyncAlt} from '@fortawesome/pro-solid-svg-icons';
import {Spec, SpecGroup} from '@webmate-web-core/shared/types/testmgmt';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {ProjectId, SpecGroupId, SpecId} from '@webmate-web-core/shared/types/ids';
import {map, switchMap, tap} from 'rxjs/operators';
import {forkJoin, of} from 'rxjs';
import {calculateSpecForest, retrieveSpecGroupsHelper, SpecForestNode} from '@webmate-web-core/shared/services/specs';
import {TreeNode} from 'primeng/api';
import {faTimes} from '@fortawesome/pro-light-svg-icons';

interface BackendSpecForestNode {
  node: { specId?: SpecId; specGroupId?: SpecGroupId };
  children: BackendSpecForestNode[];
}

interface SpecCollection {
  specIds?: SpecId[];
}

function isBackendSpec(n: BackendSpecForestNode): n is BackendSpecForestNode & { node: { specId: SpecId } } {
  return !!n.node && typeof (n.node as any).specId === 'string';
}

function isBackendGroup(n: BackendSpecForestNode): n is BackendSpecForestNode & { node: { specGroupId: SpecGroupId } } {
  return !!n.node && typeof (n.node as any).specGroupId === 'string';
}

@Component({
    selector: 'wm-spec-chooser',
    templateUrl: './spec-chooser.component.html',
    styleUrls: ['./spec-chooser.component.less']
})
export class SpecChooserComponent {
    constructor(private testService: TestService) {
    }

    @Input()
    projectId: ProjectId;

    /**
     * Emits the selected specs when the user clicks the "Use selected specs" button. May also be empty if the user cancels the modal.
     */
    @Output()
    onSpecsSelected: EventEmitter<SpecId[]> = new EventEmitter<SpecId[]>();

    @Input()
    availableSpecs?: SpecId[];

    @Input()
    useProjectSpecGroups: boolean = false;

    specTree?: TreeNode[];

    specs: {[specId: string]: Spec} = {};

    specGroups: {[specGroupId: string]: SpecGroup} = {};

    selectedSpecs: TreeNode[] = [];

    loading: boolean = true;

    isError = false;

    /** Collect the IDs we need labels for (only for the nodes we’re about to render). */
    private collectIdsFromBackendForest(nodes: BackendSpecForestNode[]): { groupIds: SpecGroupId[]; specIds: SpecId[] } {
        const groupIds = new Set<SpecGroupId>();
        const specIds  = new Set<SpecId>();
        const stack: BackendSpecForestNode[] = [...nodes];

        while (stack.length) {
            const n = stack.pop()!;
            if (isBackendGroup(n)) {
                groupIds.add(n.node.specGroupId);
            } else if (isBackendSpec(n)) {
                specIds.add(n.node.specId);
            }
            stack.push(...(n.children || []));
        }

        return { groupIds: [...groupIds], specIds: [...specIds] };
    }

    /** Ensure we have Spec/SpecGroup details for the given nodes; update caches. */
    private ensureDetailsLoadedForBackendNodes(nodes: BackendSpecForestNode[]) {
        const { groupIds, specIds } = this.collectIdsFromBackendForest(nodes);

        const missingGroupIds = groupIds.filter(id => !this.specGroups[id]);
        const missingSpecIds  = specIds.filter(id => !this.specs[id]);

        const groups$ = missingGroupIds.length
            ? retrieveSpecGroupsHelper([], missingGroupIds, this.testService)
            : of<SpecGroup[]>([]);

        const specs$  = missingSpecIds.length
            ? this.testService.getSpecs$(missingSpecIds)
            : of<Spec[]>([]);

        return forkJoin([groups$, specs$]).pipe(
            tap(([groups, specs]) => {
                for (const g of groups) {
                    this.specGroups[g.id] = g;
                }
                for (const s of specs) {
                    this.specs[s.id] = s;
                }
            }),
            map(() => void 0)
        );
    }

    private extractRootSpecIdsFromCollections(collections: SpecCollection[]): SpecId[] {
        const result: SpecId[] = [];
        const seen = new Set<SpecId>();
        const pushFromArray = (values: SpecId[] | undefined) => {
            if (!values) {
                return;
            }
            for (const value of values) {
                if (seen.has(value)) {
                    continue;
                }
                seen.add(value);
                result.push(value);
            }
        };

        for (const collection of collections || []) {
            pushFromArray(collection.specIds);
        }

        return result;
    }

    private findSpecNodeInBackendForest(nodes: BackendSpecForestNode[], specId: SpecId): BackendSpecForestNode | undefined {
        const stack: BackendSpecForestNode[] = [...nodes];
        while (stack.length) {
            const node = stack.pop()!;
            if (isBackendSpec(node) && node.node.specId === specId) {
                return node;
            }
            stack.push(...(node.children || []));
        }
        return undefined;
    }

    private pickRootSpecNodesFromForest(forest: BackendSpecForestNode[], rootSpecIds: SpecId[]): BackendSpecForestNode[] {
        const rootNodes: BackendSpecForestNode[] = [];
        for (const specId of rootSpecIds) {
            const node = this.findSpecNodeInBackendForest(forest, specId);
            if (node) {
                rootNodes.push(node);
            }
        }
        return rootNodes;
    }

    /** Map backend forest nodes -> PrimeNG TreeNode[], using cached labels; optionally lazy for groups. */
    private toTreeNodesFromBackend(nodes: BackendSpecForestNode[], opts: { lazyGroups: boolean }): TreeNode[] {
        const { lazyGroups } = opts;
        return nodes.map(n => {
            if (isBackendSpec(n)) {
                const id = n.node.specId;
                const spec = this.specs[id];
                const hasChildren = !!n.children?.length;
                const base: TreeNode = {
                    key: id,
                    label: spec ? spec.title : id,
                    data: {kind: 'spec', specId: id, spec},
                    leaf: !hasChildren
                };

                if (!hasChildren || lazyGroups) {
                    return base;
                }

                return {...base, children: this.toTreeNodesFromBackend(n.children, opts)};
            }

            if (isBackendGroup(n)) {
                const id = n.node.specGroupId;
                const group = this.specGroups[id];
                const base: TreeNode = {
                    key: id,
                    label: group ? group.name : id,
                    data: {kind: 'group', specGroupId: id, group},
                    leaf: false
                };
                // If we’re rendering lazily, don’t attach children yet
                return lazyGroups ? base : {...base, children: this.toTreeNodesFromBackend(n.children, opts)};
            }

            // Defensive fallback; shouldn't happen
            return {key: 'unknown', label: 'Unknown', leaf: true} as TreeNode;
        });
    }

    ngOnInit(): void {
        if (this.useProjectSpecGroups) {
            this.loading = true;
            this.testService.getSpecCollectionsInProject$(this.projectId).pipe(
                map((collections: SpecCollection[]) => this.extractRootSpecIdsFromCollections(collections)),
                switchMap((collectionSpecIds: SpecId[]) => {
                    if (collectionSpecIds.length === 0) {
                        return of({specs: [] as Spec[], groups: [] as SpecGroup[]});
                    }
                    return this.testService.getSpecs$(collectionSpecIds).pipe(
                        switchMap((specs: Spec[]) => {
                            return retrieveSpecGroupsHelper(
                                [],
                                specs.map(s => s.groupId).filter((g): g is SpecGroupId => !!g),
                                this.testService
                            ).pipe(map(groups => ({specs, groups})));
                        })
                    );
                })
            ).subscribe({
                next: ({specs, groups}: {specs: Spec[]; groups: SpecGroup[]}) => {
                    this.specs = {};
                    this.specGroups = {};
                    specs.forEach(spec => {
                        this.specs[spec.id] = spec;
                    });
                    groups.forEach(group => {
                        this.specGroups[group.id] = group;
                    });
                    this.specTree = this.makeSpecTree(specs, groups);
                    this.loading = false;
                },
                error: _ => {
                    this.isError = true;
                    this.loading = false;
                }
            });
        } else {
            // keep your existing "availableSpecs" branch intact (non-lazy, local calculateSpecForest)
            if (this.availableSpecs && this.availableSpecs.length !== 0) {
                this.testService.getSpecs$(this.availableSpecs).pipe(
                    switchMap((specs: Spec[]) => {
                        return retrieveSpecGroupsHelper([], specs.map(s => s.groupId).filter((g): g is SpecGroupId => !!g), this.testService)
                            .pipe(map(groups => ({specs, groups})));
                    })
                ).subscribe({
                    next: ({specs, groups}) => {
                        this.specs = {};
                        this.specGroups = {};
                        specs.forEach(spec => {
                            this.specs[spec.id] = spec;
                        });
                        groups.forEach(group => {
                            this.specGroups[group.id] = group;
                        });
                        this.specTree = this.makeSpecTree(specs, groups); // your existing local path
                    },
                    error: _ => {
                        this.isError = true;
                    }
                });
            }
        }
    }

    /**
     * Maps SpecForestNode[] coming from backend (or local calc) into PrimeNG TreeNode[].
     * - If lazyGroups = true: groups are shown as expandable with no children attached yet.
     * - If lazyGroups = false: groups get their (recursively mapped) children immediately.
     */
    makeSpecTreeFromSpecForest(
      specForest: SpecForestNode[],
      opts: { lazyGroups?: boolean } = {}
    ): TreeNode[] {
      const { lazyGroups = false } = opts;

      const mapNode = (n: SpecForestNode): TreeNode => {
        if (n.type === 'item') {
          const s = n.node as Spec;
          return {
            key: s.id,
            label: s.title,
            data: { kind: 'spec', spec: s },
            leaf: true
          };
        } else {
          const g = n.node as SpecGroup;
          const base: TreeNode = {
            key: g.id,
            label: g.name,
            data: { kind: 'group', group: g },
            leaf: false
          };

          if (lazyGroups) {
            // Don’t attach children yet; P-Tree will call loadChildren on expand
            return base;
          } else {
            // Eagerly attach children
            return {
              ...base,
              children: n.children.map(mapNode)
            };
          }
        }
      };

      return specForest.map(mapNode);
    }

    loadChildren(node: TreeNode) {
        if (node.leaf || (node.children?.length)) {
            return; // already loaded or it's a spec
        }
        if (node.data?.kind !== 'group') {
            return;
        }

        node.loading = true;
        const specGroupId = node.key as SpecGroupId;

        this.testService.getSpecForestInProject$(this.projectId, specGroupId, 2, true).pipe(
            map((forest: BackendSpecForestNode[]) => {
                // If backend returns the root group, unwrap its children; otherwise use array as-is
                if (forest.length === 1 && isBackendGroup(forest[0])) {
                    return forest[0].children;
                }
                return forest;
            }),
            switchMap((childrenForest: BackendSpecForestNode[]) =>
                this.ensureDetailsLoadedForBackendNodes(childrenForest).pipe(map(() => childrenForest))
            )
        ).subscribe({
            next: (childrenForest: BackendSpecForestNode[]) => {
                node.children = this.toTreeNodesFromBackend(childrenForest, {lazyGroups: true});
                node.loading = false;
            },
            error: _ => {
                node.loading = false;
            }
        });
    }

    makeSpecTree(specs: Spec[], groups: SpecGroup[]): TreeNode[] {
      const specForest = calculateSpecForest(specs, groups);
      // Non-lazy: when we already have all data locally, build full tree
      return this.makeSpecTreeFromSpecForest(specForest, { lazyGroups: false });
    }

    // Optional: a small flag if you want to show a spinner on the "Use selected specs" button
    loadingSelection = false;

    private dedupe(ids: SpecId[]): SpecId[] {
        return Array.from(new Set(ids));
    }

    private extractSpecIdsFromTreeNode(node: TreeNode): SpecId[] {
        const out: SpecId[] = [];
        const stack: TreeNode[] = [node];

        while (stack.length) {
            const current = stack.pop()!;
            if (current.data?.kind === 'spec') {
                out.push(current.key as SpecId);
            }
            if (current.children?.length) {
                stack.push(...current.children);
            }
        }

        return out;
    }

    useSelectedSpecs() {
        const selected = this.selectedSpecs ?? [];
        const selectedSpecIds = selected.flatMap(node => this.extractSpecIdsFromTreeNode(node));
        this.onSpecsSelected.emit(this.dedupe(selectedSpecIds));
    }


    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faCheck = faCheck;
    protected readonly faTimes = faTimes;
}
